import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// Task #6707 — homepage "pick up where you left off" personalization.
// Recently-viewed cars are tracked client-side only (localStorage) as small
// display snapshots taken on the car-detail page, so the homepage can render
// the rail without N extra API calls.
//
// Task #6830 — the snapshots can go stale (listing renamed, delisted, photo
// changed). Callers that render the chips can opt into a single batch
// validation (`{ validate: true }`) which asks the server which ids are still
// live and refreshes label/thumbnail fields, pruning delisted cars from both
// the rendered strip and localStorage. Callers gate `validate` on having a
// session so logged-out users never trigger the call; the hook itself skips
// it when there is no history.

const STORAGE_KEY = "bookaclassic_recently_viewed";
const MAX_RECENT = 8;

// Throttle: validate at most once per page load window per tab. The strip can
// remount (e.g. client-side nav back to the homepage) — re-validating every
// few seconds adds nothing, listings don't change that fast.
const VALIDATE_TTL_MS = 5 * 60 * 1000;
let lastValidatedAt = 0;

export interface RecentlyViewedCar {
  id: number;
  slug: string;
  make: string;
  model: string;
  year: number | null;
  city?: string | null;
  country_code?: string | null;
  image?: string | null;
  timestamp: number;
}

function loadEntries(): RecentlyViewedCar[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is RecentlyViewedCar =>
          !!e && typeof e === "object" && typeof e.id === "number" && typeof e.slug === "string",
      )
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function saveEntries(entries: RecentlyViewedCar[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_RECENT)));
  } catch {
    // localStorage full or unavailable — tracking is best-effort.
  }
}

/** Record a car view. Plain function (not a hook) so the car-detail page can
 *  call it from an effect without subscribing to the list. */
export function recordRecentlyViewedCar(entry: Omit<RecentlyViewedCar, "timestamp">) {
  const existing = loadEntries().filter((e) => e.id !== entry.id);
  saveEntries([{ ...entry, timestamp: Date.now() }, ...existing]);
}

/** Test-only: reset the validation throttle between tests. */
export function _resetRecentlyViewedValidationThrottle() {
  lastValidatedAt = 0;
}

export function useRecentlyViewedCars(options?: { validate?: boolean }) {
  const validate = options?.validate === true;
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedCar[]>(loadEntries);

  useEffect(() => {
    if (!validate) return;
    const entries = loadEntries();
    if (entries.length === 0) return;
    if (Date.now() - lastValidatedAt < VALIDATE_TTL_MS) return;

    let cancelled = false;
    api
      .getCarChips(entries.map((e) => e.id))
      .then(({ cars }) => {
        if (cancelled) return;
        // Arm the throttle only once a response was actually APPLIED — an
        // unmount-cancelled or failed validation must not suppress the next
        // mount's retry for the whole TTL, or stale chips would linger.
        lastValidatedAt = Date.now();
        const freshById = new Map(cars.map((c) => [c.id, c]));
        const reconciled = entries
          .filter((e) => freshById.has(e.id))
          .map((e) => {
            const fresh = freshById.get(e.id)!;
            return {
              ...e,
              slug: fresh.slug,
              make: fresh.make,
              model: fresh.model,
              year: fresh.year,
              city: fresh.city,
              country_code: fresh.country_code,
              // Only take a server image when one resolves — a transient
              // image gap shouldn't blank a chip that has a stored thumb.
              image: fresh.image ?? e.image ?? null,
            };
          });
        saveEntries(reconciled);
        setRecentlyViewed(reconciled);
      })
      .catch(() => {
        // Validation is best-effort: on failure keep rendering the stored
        // snapshots (the link still resolves via the live listing page).
        // Allow a retry on the next mount.
        lastValidatedAt = 0;
      });
    return () => {
      cancelled = true;
    };
  }, [validate]);

  const clearRecentlyViewed = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setRecentlyViewed([]);
  }, []);

  return { recentlyViewed, clearRecentlyViewed };
}
